import React from 'react';
import PropTypes from 'prop-types';
import { DropdownMenu } from '@spark';
import FieldLabel from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/FieldLabel';
import {
    ContentTypeField,
    LanguageField,
    TextLengthField,
    ToneStyleField,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/SettingFields';
import {
    DOMAIN_OPTIONS,
    HELP_TEXTS,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';

/** Initial advanced-settings state (Figma defaults: English / Short / Text Only). */
export const createDefaultSettings = () => ({
    domain: null,
    languages: ['English'],
    toneStyles: [],
    textLength: 'short',
    contentType: 'text_only',
    customGuidelines: '',
});

export const settingsShape = PropTypes.shape({
    domain: PropTypes.string,
    languages: PropTypes.arrayOf(PropTypes.string),
    toneStyles: PropTypes.arrayOf(PropTypes.string),
    textLength: PropTypes.string,
    contentType: PropTypes.string,
    customGuidelines: PropTypes.string,
});

/** "Select Domain" dropdown (Figma Advanced settings). */
export const DomainField = ({ value, onChange }) => (
    <div className="gdai-field">
        <FieldLabel text="Select Domain" help={HELP_TEXTS.domain} />
        <DropdownMenu
            selectType="single-select"
            size="medium"
            fill
            showLabel={false}
            placeholder="Select"
            text={value || undefined}
            value={value || undefined}
            items={DOMAIN_OPTIONS.map((option) => ({ label: option }))}
            onSelect={(item) => onChange(item.label)}
            showClearButton
            onClear={() => onChange(null)}
        />
    </div>
);

DomainField.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
};

/**
 * "Advanced settings" section on the Define Task step, shared by every
 * Generate-with-AI flow and the Retrieval Agent (Figma Data Creation):
 * Select Domain (plus an optional provider field beside it), Supported
 * languages / Tone & Style, Text length / Content Type. Flow-specific extras
 * (the Retrieval Agent's Exclude Datasets) render as children below the grid.
 */
const AdvancedSettingsSection = ({
    domain,
    onDomainChange,
    languages,
    onLanguagesChange,
    toneStyles,
    onToneStylesChange,
    textLength,
    onTextLengthChange,
    contentType,
    onContentTypeChange,
    providerField = null,
    showTextLengthOptionHelp = false,
    children,
}) => (
    <div className="gdai-section">
        <div className="gdai-section-heading fs-unmask">Advanced settings</div>
        {providerField ? (
            <div className="gdai-row">
                <DomainField value={domain} onChange={onDomainChange} />
                {providerField}
            </div>
        ) : (
            <DomainField value={domain} onChange={onDomainChange} />
        )}
        <div className="gdai-row">
            <LanguageField value={languages} onChange={onLanguagesChange} />
            <ToneStyleField value={toneStyles} onChange={onToneStylesChange} />
        </div>
        <div className="gdai-row">
            <TextLengthField
                value={textLength}
                onChange={onTextLengthChange}
                showOptionHelp={showTextLengthOptionHelp}
            />
            <ContentTypeField value={contentType} onChange={onContentTypeChange} />
        </div>
        {children}
    </div>
);

AdvancedSettingsSection.propTypes = {
    domain: PropTypes.string,
    onDomainChange: PropTypes.func.isRequired,
    languages: PropTypes.arrayOf(PropTypes.string),
    onLanguagesChange: PropTypes.func.isRequired,
    toneStyles: PropTypes.arrayOf(PropTypes.string),
    onToneStylesChange: PropTypes.func.isRequired,
    textLength: PropTypes.string,
    onTextLengthChange: PropTypes.func.isRequired,
    contentType: PropTypes.string,
    onContentTypeChange: PropTypes.func.isRequired,
    providerField: PropTypes.node,
    showTextLengthOptionHelp: PropTypes.bool,
    children: PropTypes.node,
};

export default AdvancedSettingsSection;
